import type { ReactNode } from 'react';

import type { PageLayoutProps } from '@spotify/confidence-core-react';

import { HeaderLayout, PageHeaderActions } from '../Header';
import { PageBreadcrumbs } from '../PageBreadcrumbs';
import type { PageRootBridgeProps } from '../types';
import type { PageContentProps } from './types';

type PageHeaderLayoutProps = Pick<
  PageLayoutProps,
  | 'enableMargin'
  | 'headerBreadcrumbs'
  | 'headerContent'
  | 'isLoading'
  | 'pageHeader'
  | 'pageTitleOverride'
  | 'stickyHeader'
  | 'subheader'
  | 'title'
>;

type PageHeaderBridgeProps = Pick<
  PageRootBridgeProps,
  | 'ancestors'
  | 'descriptor'
  | 'hideTitle'
  | 'leafLabel'
  | 'pageHeader'
  | 'titleAdornment'
>;

type PageHeaderContentProps = Pick<
  PageContentProps,
  | 'breadcrumbActions'
  | 'enableMargin'
  | 'headerContent'
  | 'isLoading'
  | 'narrow'
  | 'stickyHeader'
  | 'subheader'
>;

type ResolvePageHeaderLayoutProps = {
  page: PageHeaderBridgeProps;
  content: PageHeaderContentProps;
};

const resolveActionContent = (
  actionSlot: PageHeaderActions[keyof PageHeaderActions],
  fallback: ReactNode,
): ReactNode => (actionSlot ? actionSlot.content : fallback);

export const resolvePageHeaderLayoutProps = ({
  page: {
    ancestors,
    descriptor,
    hideTitle,
    leafLabel,
    pageHeader,
    titleAdornment,
  },
  content: {
    breadcrumbActions,
    enableMargin,
    headerContent,
    isLoading,
    narrow,
    stickyHeader,
    subheader,
  },
}: ResolvePageHeaderLayoutProps): PageHeaderLayoutProps => {
  const pageTitle =
    typeof leafLabel === 'string' ? leafLabel : descriptor.title;
  const defaultTitle = titleAdornment ? (
    <span>
      {leafLabel}
      {titleAdornment}
    </span>
  ) : (
    leafLabel
  );
  const title = hideTitle ? null : defaultTitle;

  if (!pageHeader) {
    return {
      title,
      pageTitleOverride: pageTitle,
      headerBreadcrumbs: <PageBreadcrumbs actions={breadcrumbActions} />,
      headerContent,
      enableMargin,
      isLoading,
      stickyHeader,
      subheader,
    };
  }

  return {
    pageHeader: (
      <HeaderLayout
        title={title}
        pageTitle={pageTitle}
        breadcrumbs={<PageBreadcrumbs bottomSpacing="none" />}
        hasBreadcrumbs={ancestors.length > 0}
        hideTitle={hideTitle}
        primaryActions={resolveActionContent(
          pageHeader.primaryActions,
          headerContent,
        )}
        secondaryActions={resolveActionContent(
          pageHeader.secondaryActions,
          breadcrumbActions,
        )}
        enableMargin={enableMargin}
        narrow={narrow}
        isLoading={isLoading}
        stickyHeader={stickyHeader}
        subheader={subheader}
      />
    ),
  };
};
